import { Flex } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CheckCircleSolid, LightBulbSolid, rem, Text } from '@bonusly/design-system';

import { Bonus } from '../../../../../../types/bonus';
import { useAddToDiscussion } from '../../hooks/use-add-to-discussion';
import { SuggestedTopicDTO } from '../../lib/entities/suggested-topic';

type DiscussionIdeaCardProps = {
	suggestedTopic: SuggestedTopicDTO;
	meetingId: string;
	addToCategoryId: string;
	bonuses: Bonus[];
};

export const DiscussionIdeaCard: FC<DiscussionIdeaCardProps> = ({ suggestedTopic, meetingId, addToCategoryId, bonuses }) => {
	const { t } = useTranslation('one_on_ones');

	const { handleAddToDiscussion, isLoading } = useAddToDiscussion(suggestedTopic.id, meetingId, addToCategoryId, suggestedTopic.summary);

	const suggestedTopicBonuses = suggestedTopic.bonusIds.map((bonusId) => bonuses?.find((bonus) => bonus.id === bonusId));

	const hasAgendaTopic = suggestedTopic.agendaTopic !== null;

	return (
		<Flex
			flexDirection="column"
			width={rem(300)}
			borderRadius="lg"
			borderColor="border.default"
			padding={4}
			mb={4}
			bg="whiteAlpha.900"
			justifyContent="space-between"
			height="100%"
		>
			<Flex gap={2} alignItems="center" borderBottom="solid" paddingBottom={3} borderColor="border.soft">
				<Flex backgroundColor="warning.bg" borderRadius="full" padding="4.8px">
					<LightBulbSolid color="warning.icon" />
				</Flex>

				<Text size="lg" fontWeight="semibold">
					{t('meeting.carousel.discussion_idea.title')}
				</Text>
			</Flex>

			<Text color="text.default">{suggestedTopic.summary}</Text>

			<Flex direction="column" gap={2}>
				{suggestedTopicBonuses.length > 0 && (
					<Text color="text.softest" size="sm">
						{t('meeting.carousel.discussion_idea.based_on', {
							giverName: suggestedTopicBonuses[0]?.giver.display_name ?? '',
						})}
						{suggestedTopicBonuses.length > 1 &&
							t('meeting.carousel.discussion_idea.and_more', {
								count: suggestedTopicBonuses.length - 1,
							})}
					</Text>
				)}

				<Button
					colorScheme="primary"
					variant="alpha"
					alignItems="center"
					w="100%"
					size="sm"
					gap={1}
					mt="auto"
					isActive={hasAgendaTopic}
					isLoading={isLoading}
					onClick={() => {
						if (hasAgendaTopic) {
							return;
						}
						handleAddToDiscussion();
					}}
				>
					{hasAgendaTopic ? (
						<>
							<CheckCircleSolid size="sm" />
							{t('meeting.carousel.discussion_idea.added_to_discussion')}
						</>
					) : (
						<>{t('meeting.carousel.discussion_idea.add_to_discussion')}</>
					)}
				</Button>
			</Flex>
		</Flex>
	);
};
